/**
 * CWR 1.2 — ClientShell → tab-page context.
 *
 * The ClientShell is the SINGLE owner of the hub-session + dashboard read (the
 * 1.1 payload carries business + user + body data). It exposes the ready
 * dashboard and a shared book handler to its tab children via the router
 * Outlet, so the header, FAB, and Home body never re-fetch or drift. Children
 * read it with `useClientShellContext()` — they never call `useDashboard`
 * directly. This realizes the `useClientShellDashboard` wrapper idea.
 */
import type { ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { BookingHubDashboard } from '@/types/dashboard';

export interface ClientShellContext {
  /** The ready dashboard payload (GET /api/webapp/booking-hub) — never null inside a tab page. */
  dashboard: BookingHubDashboard;
  /**
   * Availability-aware "book a session" action shared by the FAB and Home's
   * Book button — navigates via resolveBookTarget (+ recipient token) or shows
   * a toast when nothing is bookable.
   */
  onBook: () => void;
  /**
   * CWR 2.2 — a shell child renders its OWN header into the shell's
   * (non-scrolling) header region (e.g. the catalog's back + title + tabs).
   * `null` restores the default greeting/tab-title header.
   *
   * Usage: call inside a `useLayoutEffect` (no flash) with PRIMITIVE deps so it
   * re-sets only when the header content changes, and return `setHeaderSlot(null)`
   * to clear on unmount. Do NOT depend the effect on the JSX node itself — a
   * fresh element each render would loop.
   */
  setHeaderSlot: (node: ReactNode | null) => void;
  /**
   * Reset the shell's persistent `<main>` scroller to the top. The shell already
   * does this on every route change; children call it for IN-PAGE view swaps
   * that change content without changing the route (e.g. the catalog's
   * Services/Events tabs), so the new view starts at the top with no leak.
   */
  scrollContentToTop: () => void;
}

export function useClientShellContext(): ClientShellContext {
  return useOutletContext<ClientShellContext>();
}
